import React, { useState, useCallback } from 'react';
import { isEmpty, noop } from 'lodash';
import PropTypes from 'prop-types';
import { CollapsibleList, useDebouncedValue, useElementSize } from 'taboola-ultimate-ui';
import { withIndication } from 'modules/errors/components';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import TARGETING_TYPES from '../../../config/TargetingTypes';
import { useSendSegmentSearchEvent } from '../../../utils/useSendSegmentSearchEvent';
import { TargetingTypeBareDropdown } from '../../TargetingTypeDropdown';
import { LoadingMode, MAX_SEGMENTS_TO_SELECT } from '../../Tree';
import {
    excludeErrorListValidations,
    excludeWarningListValidations,
    includeErrorListValidations,
    includeWarningListValidations,
} from '../MarketplaceAudienceValidations';
import { MarketplaceAudiencesProvider } from '../MarketplaceAudiencesContext';
import ProvidersDropdown from '../ProvidersDropdown/ProvidersDropDown';
import SegmentListItemContentWithValidations from '../SegmentListItemContent/SegmentListItemContentWithValidations';
import SegmentsDropdown from '../SegmentsDropdown/SegmentsDropdown';
import {
    useProvidersApi,
    useProvidersState,
    useSegmentsApi,
    useSelectedSegments,
    useTaxonomiesResources,
} from '../hooks';
import { useMarketplaceAudienceTargetingEffects } from '../hooks/useMarketplaceAudienceTargetingEffects';
import { useNodeHandlersWithMetrics } from '../hooks/useNodeHandlersWithMetrics';
import { useMarketplaceAudiencesTreeState } from './hooks';
import { useMarketplaceAudiencesTargetingFieldNames } from './hooks/useMarketplaceAudienceTargetingFieldNames';
import commonStyles from '../../commonEditor.module.scss';
import styles from './marketplaceAudiencesTargeting.module.scss';

const CollapsibleListWithIndication = withIndication(CollapsibleList);

const MarketplaceAudiencesTargeting = ({ selectedAccountId }) => {
    const { value: campaignId } = useFormFieldValue({ field: 'id' });
    const [targetingType, setTargetingType] = useState(TARGETING_TYPES.INCLUDE);
    const [searchText, setSearchText] = useState('');
    const { formatMessage } = useIntl();
    const search = useDebouncedValue(searchText.length > 2 ? searchText : '');

    const { handleAddMissingTargetingGroup, includeIdsFormFieldName, excludeIdsFormFieldName } =
        useMarketplaceAudiencesTargetingFieldNames();

    const { providers, reloadProviders, isProvidersReady } = useProvidersApi({ selectedAccountId });
    const {
        selectedProviders,
        isProviderEnabled,
        getPartnerByProvider,
        enabledPartnerGroup,
        handleChangeSelectedProviders,
        handleChangeEnabledProviders,
        setDefaultEnabledProviders,
        enabledProvidersMap,
    } = useProvidersState({ providers });
    const hasFilter = !isEmpty(selectedProviders);

    const { segments, ...treeStateParams } = useSegmentsApi({
        search,
        providers: selectedProviders,
        selectedAccountId,
    });

    const { getTaxonomyValue, reloadTaxonomies } = useTaxonomiesResources({ selectedAccountId });
    const isDisabledLeaf = useCallback(({ provider }) => !isProviderEnabled(provider), [isProviderEnabled]);

    const {
        selectedLeaves,
        handleRemoveItem,
        handleReload,
        setSelectedLeavesByIds,
        handleSelectNode,
        handleSelectAll,
        expandAll,
        setIsReady,
        ...treeProps
    } = useMarketplaceAudiencesTreeState({
        search,
        leaves: segments,
        getTaxonomyValue,
        formatMessage,
        isDisabledLeaf,
        hasFilter,
        ...treeStateParams,
    });

    const {
        includeLeaves,
        excludeLeaves,
        removeIncludeLeaves,
        removeExcludeLeaves,
        includeFailedValidationData,
        excludeFailedValidationData,
        scrollRef,
    } = useSelectedSegments({
        targetingType,
        selectedLeaves,
        setSelectedLeavesByIds,
        includeIdsFormFieldName,
        excludeIdsFormFieldName,
        onAddIds: handleAddMissingTargetingGroup,
        enabledProvidersMap,
        includeErrorListValidations,
        includeWarningListValidations,
        excludeErrorListValidations,
        excludeWarningListValidations,
        setIsReady,
        isReadyToValidate: treeProps.isReady && isProvidersReady,
    });

    useMarketplaceAudienceTargetingEffects({
        handleReload,
        reloadProviders,
        reloadTaxonomies,
        handleChangeEnabledProviders,
        selectedLeaves,
        search,
        expandAll,
    });

    useSendSegmentSearchEvent({
        component: 'Marketplace Audiences Segments Search',
        searchText: search,
        resultsCount: segments.length,
        providers: selectedProviders,
        isLoading: treeProps.loadingMode !== LoadingMode.NONE,
    });

    const { handleSelectNodeWrapper, handleSelectAllWrapper } = useNodeHandlersWithMetrics({
        handleSelectNode,
        handleSelectAll,
        search,
        selectedLeaves,
        setDefaultEnabledProviders,
    });

    const { width, ref } = useElementSize();

    return (
        <div className={styles['container']} data-testid="marketplace-audiences-targeting">
            <div className={styles['dropdown-container']} ref={ref}>
                <MarketplaceAudiencesProvider
                    limit={MAX_SEGMENTS_TO_SELECT}
                    handleSelectNode={handleSelectNodeWrapper}
                    handleSelectAll={handleSelectAllWrapper}
                    isProviderEnabled={isProviderEnabled}
                    getPartnerByProvider={getPartnerByProvider}
                    enabledPartnerGroup={enabledPartnerGroup}
                    campaignId={campaignId}
                    {...treeProps}
                >
                    <TargetingTypeBareDropdown
                        onChange={({ value }) => setTargetingType(value)}
                        value={targetingType}
                        aria-label="toggle targeting type dropdown"
                    />
                    <div className={styles['delimiter']} />
                    <SegmentsDropdown search={searchText} onSearchChange={text => setSearchText(text)} width={width} />
                    <ProvidersDropdown
                        selectedValues={selectedProviders}
                        options={providers}
                        onChange={providerIds => handleChangeSelectedProviders(providerIds)}
                    />
                </MarketplaceAudiencesProvider>
            </div>
            <CollapsibleListWithIndication
                id="campaignTargeting.marketplaceAudienceTargeting.include"
                items={includeLeaves}
                ItemComponent={SegmentListItemContentWithValidations}
                metadata={{
                    targetingType: TARGETING_TYPES.INCLUDE,
                    field: includeIdsFormFieldName,
                    enabledProvidersMap,
                    isProvidersReady,
                }}
                listHeaderTitle={
                    <FormattedMessage
                        id="campaign.editor.marketplace.include.audiences.title"
                        defaultMessage="Included Marketplace Audiences"
                    />
                }
                deleteItem={handleRemoveItem}
                clearItems={removeIncludeLeaves}
                showAllLabel={<FormattedMessage id="app.actionButtons.show.all" defaultMessage="Show all" />}
                showLessLabel={<FormattedMessage id="app.actionButtons.show.less" defaultMessage="Show less" />}
                clearAllLabel={<FormattedMessage id="app.actionButtons.clear.all" defaultMessage="Clear All" />}
                {...includeFailedValidationData}
                indicationContainerClass={commonStyles['warning-indication']}
                ref={scrollRef}
            />
            <CollapsibleListWithIndication
                id="campaignTargeting.marketplaceAudienceTargeting.exclude"
                items={excludeLeaves}
                ItemComponent={SegmentListItemContentWithValidations}
                metadata={{
                    targetingType: TARGETING_TYPES.EXCLUDE,
                    field: excludeIdsFormFieldName,
                    enabledProvidersMap,
                    isProvidersReady,
                }}
                listHeaderTitle={
                    <FormattedMessage
                        id="campaign.editor.marketplace.exclude.audiences.title"
                        defaultMessage="Excluded Marketplace Audiences"
                    />
                }
                addItem={noop}
                deleteItem={handleRemoveItem}
                clearItems={removeExcludeLeaves}
                showLessLabel={<FormattedMessage id="app.actionButtons.show.less" defaultMessage="Show less" />}
                showAllLabel={<FormattedMessage id="app.actionButtons.show.all" defaultMessage="Show all" />}
                clearAllLabel={<FormattedMessage id="app.actionButtons.clear.all" defaultMessage="Clear All" />}
                {...excludeFailedValidationData}
            />
        </div>
    );
};

MarketplaceAudiencesTargeting.propTypes = {
    selectedAccountId: PropTypes.string,
};

export { MarketplaceAudiencesTargeting };
